import React from "react";
import { Redirect } from "react-router-dom";
import { Input, Segment } from "semantic-ui-react";
import styled from "styled-components";
import { Constants, ErrorMessages, Paths } from "../Constants";
import dateTimeHelper from "../util/dateTimeHelper";
import ErrorManager from "../util/ErrorManager";
import { isPasswordFormatValid } from "../util/regexHelper";
import stringHelper from "../util/stringHelper";
import BaseComponent from "./BaseComponent";
import AppHeaderSmall from "./common/AppHeaderSmall";
import Footer from "./common/Footer";
import MainContentWrapper from "./common/MainContentWrapper";
import {
  default as ErrorDisplay,
  default as PillButton,
} from "./common/PillButton";
import SFCookieConsent from "./common/SFCookieConsent";
import SFLoader from "./common/SFLoader";
import SiteWrapper from "./common/SiteWrapper";
import TabBar from "./common/TabBar";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
`;

const PersonalInfo = styled.div`
  font-size: 16px;
  padding-bottom: 5px;
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-top: 10px;
`;

const SectionLabel = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

class Settings extends BaseComponent {
  constructor() {
    super();
    this.state = {
      showChangePassword: false,
      showChangeUserName: false,
      userName: "",
      newUserName: null,
      showActivityIndicator: false,
      oldPassword: "",
      newPassword: "",
      newPasswordConfirm: "",
      consumer: {
        firstName: "",
        middleName: "",
        lastName: "",
        dob: "",
        postalCode: "",
      },
    };

    this.userNameErrorManager = new ErrorManager();
    this.passwordErrorManager = new ErrorManager();

    this.wipe = this.wipe.bind(this);
    this.logout = this.logout.bind(this);
    this.removeTokens = this.removeTokens.bind(this);
    this.changeUserName = this.changeUserName.bind(this);
    this.getCurrentUserInfo = this.getCurrentUserInfo.bind(this);
    this.changePassword = this.changePassword.bind(this);
  }

  async removeTokens() {
    window.localStorage.removeItem(Constants.PASSWORD_KEY);
    window.localStorage.removeItem(Constants.CLIENT_TOKEN_EXPIRATION);
    window.localStorage.removeItem(Constants.CLIENT_TOKEN_KEY);
    window.localStorage.removeItem(Constants.USER_TOKEN_EXPIRATION);
    window.localStorage.removeItem(Constants.USER_TOKEN_KEY);
    window.localStorage.removeItem(Constants.TESTING_RULES_GUIDELINES_READ_KEY);
    window.localStorage.removeItem(Constants.LOCAL_AUTHENTICATION_ENABLED_KEY);
  }

  async changeUserName() {
    if (!(await this.checkLogin())) {
      return;
    }

    try {
      this.userNameErrorManager.reset();
      this.setState({ showActivityIndicator: true });
      let response = await this.identityApi.validateRequestedEmail(
        this.state.newUserName
      );
      if (!response.data.available) {
        this.userNameErrorManager.addError(
          "That user name/email is not available."
        );
        this.setState({ showActivityIndicator: false });
        return;
      }
      if (!response.data.valid) {
        this.userNameErrorManager.addError(
          "User name must be a valid address."
        );
        this.setState({ showActivityIndicator: false });
        return;
      }
    } catch (err) {
      this.userNameErrorManager.addError(
        "There was an error validating the email address."
      );
      this.setState({ showActivityIndicator: false });
      return;
    }
    try {
      const success = await this.identityUserApi.changeUsername(
        this.state.newUserName
      );
      if (!success) {
        this.userNameErrorManager.addError(
          "There was an internal error changing the user name."
        );
        this.setState({ showActivityIndicator: false });
        return;
      } else {
        await this.getCurrentUserInfo();
        this.setState({
          showChangeUserName: false,
          newUserName: "",
          showActivityIndicator: false,
        });
      }
    } catch (err) {
      this.userNameErrorManager.addError(
        "There was an internal error changing the user name."
      );
      this.setState({ showActivityIndicator: false });
      return;
    }
  }

  async changePassword() {
    if (!(await this.checkLogin())) {
      return;
    }

    try {
      this.passwordErrorManager.reset();

      if (!isPasswordFormatValid(this.state.newPassword)) {
        this.passwordErrorManager.addError(ErrorMessages.PASSWORD_FORMAT);
        this.forceUpdate();
        return;
      }

      this.setState({ showActivityIndicator: true });
      const success = await this.identityUserApi.changePassword(
        this.state.oldPassword,
        this.state.newPassword
      );
      if (!success) {
        this.passwordErrorManager.addError(
          "There was a problem changing your password. Please check the spelling of you current password."
        );
        this.setState({ showActivityIndicator: false });
        return;
      } else {
        this.setState({
          showChangePassword: false,
          newPassword: "",
          oldPassword: "",
          newPasswordConfirm: "",
          showActivityIndicator: false,
        });
      }
    } catch (err) {
      this.passwordErrorManager.addError(
        "There was a problem changing your password. Please check the spelling of you current password."
      );
      this.setState({ showActivityIndicator: false });
      return;
    }
  }

  validatePasswords() {
    if (
      !stringHelper.isNullOrEmpty(this.state.newPassword) &&
      !stringHelper.isNullOrEmpty(this.state.newPasswordConfirm)
    ) {
      //somethign in there for everything. Start validating.

      if (this.state.password !== this.state.passwordConfirm) {
        return false;
      }

      return true;
    }
    return false;
  }

  async wipe() {
    await this.removeTokens();
    window.localStorage.removeItem(Constants.USERNAME_KEY);

    this.redirect(Paths.INTRO);
  }

  async logout() {
    await this.removeTokens();
    this.redirect(Paths.LOGIN);
  }

  async getCurrentUserInfo() {
    if (!(await this.checkLogin())) {
      return;
    }

    this.setState({ showActivityIndicator: true });
    const response = await this.valhallaApi.getUserInfo();
    this.setState({ userName: response.data.userName });
    const result = await this.safeFunApi.getCurrentUser();
    const consumer = result.data;
    this.setState({ consumer });
    this.setState({ showActivityIndicator: false });
  }

  componentDidMount() {
    this.getCurrentUserInfo();
  }

  render() {
    return (
      <SiteWrapper>
        {this.state.redirectPath ? (
          <Redirect
            to={{
              pathname: this.state.redirectPath,
              search: this.state.redirectSearch,
              state: this.state.redirectState,
            }}
          />
        ) : null}

        <AppHeaderSmall title="My Settings" />
        <SFLoader active={this.state.showActivityIndicator} />
        <MainContentWrapper>
          <TabBar settings />

          <Wrapper>
            <Segment style={{ width: "100%" }}>
              <Section>
                <SectionLabel>Personal Info</SectionLabel>
                <PersonalInfo>
                  {this.state.consumer.firstName +
                    (!this.state.consumer.middleName ||
                    this.state.consumer.middleName === ""
                      ? " "
                      : " " + this.state.consumer.middleName + " ") +
                    this.state.consumer.lastName}
                </PersonalInfo>
                <PersonalInfo>
                  {dateTimeHelper.formatDate(this.state.consumer.dob)}
                </PersonalInfo>
                <PersonalInfo>{this.state.consumer.postalCode}</PersonalInfo>
                <PillButton
                  style={{ width: "150px" }}
                  primary
                  onClick={() => {
                    this.redirect(Paths.PERSONAL_INFO);
                  }}
                >
                  Change
                </PillButton>
              </Section>
              <Section>
                <SectionLabel>Password</SectionLabel>
                {!this.state.showChangePassword ? (
                  <PillButton
                    style={{ width: "150px" }}
                    primary
                    onClick={() => {
                      this.setState({ showChangePassword: true });
                    }}
                    disabled={
                      this.state.showChangeUserName ||
                      this.state.showChangePassword
                    }
                  >
                    Change
                  </PillButton>
                ) : null}
                {this.state.showChangePassword ? (
                  <>
                    <Input
                      placeholder="Current Password"
                      type="password"
                      autoCapitalize="none"
                      onChange={(e) => {
                        this.setState({ oldPassword: e.target.value });
                      }}
                      value={this.state.oldPassword}
                    />
                    <Input
                      placeholder="New Password"
                      type="password"
                      autoCapitalize="none"
                      onChange={(e) => {
                        this.setState({ newPassword: e.target.value });
                      }}
                      disabled={this.state.oldPassword.length < 8}
                      value={this.state.newPassword}
                    />
                    <Input
                      type="password"
                      placeholder="Confirm New Password"
                      autoCapitalize="none"
                      onChange={(e) => {
                        this.setState({ newPasswordConfirm: e.target.value });
                      }}
                      disabled={this.state.newPassword.length < 8}
                      value={this.state.newPasswordConfirm}
                    />
                    <ButtonGroup>
                      <PillButton
                        style={{ width: "150px" }}
                        primary
                        onClick={() => {
                          this.changePassword();
                        }}
                        disabled={!this.validatePasswords()}
                      >
                        Update
                      </PillButton>

                      <PillButton
                        style={{ width: "150px" }}
                        primary
                        onClick={() => {
                          this.setState({ showChangePassword: false });
                        }}
                      >
                        Cancel
                      </PillButton>
                    </ButtonGroup>
                    {this.passwordErrorManager.getErrorCount() > 0 ? (
                      <>
                        <ErrorDisplay
                          errorManager={this.passwordErrorManager}
                        />
                      </>
                    ) : null}
                  </>
                ) : null}
              </Section>
              {this.state.showActivityIndicator ? (
                /*spt for activity indicator*/
                <></>
              ) : null}
              <Section>
                <SectionLabel>Username/Email</SectionLabel>
                <PersonalInfo style={{ marginBottom: 20 }}>
                  {this.state.userName}
                </PersonalInfo>
                {!this.state.showChangeUserName ? (
                  <PillButton
                    style={{ width: "150px" }}
                    primary
                    onClick={() => {
                      this.setState({ showChangeUserName: true });
                    }}
                    disabled={
                      this.state.showChangeUserName ||
                      this.state.showChangePassword
                    }
                  >
                    Change
                  </PillButton>
                ) : null}
                {this.state.showChangeUserName ? (
                  <>
                    <Input
                      placeholder="New Username/Email..."
                      autoCapitalize="none"
                      autoCorrect={false}
                      onChange={(e) => {
                        this.setState({ newUserName: e.target.value });
                      }}
                      value={this.state.newUserName}
                      textAlign={"center"}
                    />
                    <ButtonGroup>
                      <PillButton
                        style={{ width: "150px" }}
                        primary
                        onClick={() => {
                          this.changeUserName();
                        }}
                      >
                        Update
                      </PillButton>

                      <PillButton
                        style={{ width: "150px" }}
                        primary
                        onClick={() => {
                          this.setState({ showChangeUserName: false });
                        }}
                      >
                        Cancel
                      </PillButton>
                    </ButtonGroup>
                    {this.userNameErrorManager.getErrorCount() > 0 ? (
                      <>
                        <ErrorDisplay
                          errorManager={this.userNameErrorManager}
                        />
                      </>
                    ) : null}
                  </>
                ) : null}
              </Section>

              <Section>
                <SectionLabel>SafeFun ID</SectionLabel>
                <PersonalInfo>{this.state.consumer.safeFunId}</PersonalInfo>
              </Section>

              <Section>
                <SectionLabel>Logout</SectionLabel>
                <PillButton
                  style={{ width: "150px" }}
                  primary
                  onClick={() => {
                    this.logout();
                  }}
                >
                  Logout
                </PillButton>
              </Section>
              <Section>
                <SectionLabel>Local Data</SectionLabel>
                <PillButton
                  style={{ width: "150px" }}
                  primary
                  onClick={() => {
                    this.wipe();
                  }}
                >
                  Wipe
                </PillButton>
              </Section>
            </Segment>
          </Wrapper>
        </MainContentWrapper>
        <Footer />
        <SFCookieConsent />
      </SiteWrapper>
    );
  }
}

export default Settings;
